import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ai-websites-2/artifacts/idq8yn2y_WhatsApp%20Image%202026-03-09%20at%2009.42.59.jpeg";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-white/5">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src={LOGO_URL} alt="AG Collective" className="h-7 w-auto invert" />
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-4xl font-medium mb-4">Política de Privacidad</h1>
        <p className="text-neutral-500 mb-12">Última actualización: Marzo 2024</p>

        <div className="prose prose-invert prose-neutral max-w-none">
          {/* Introducción */}
          <section className="mb-12">
            <h2 className="text-2xl font-medium mb-4 text-white">1. Introducción</h2>
            <p className="text-neutral-400 leading-relaxed mb-4">
              En AG Collective ("nosotros", "nuestro" o "la Agencia"), respetamos su privacidad y nos comprometemos a proteger sus datos personales. Esta política de privacidad le informará sobre cómo cuidamos sus datos personales cuando visita nuestro sitio web y le informará sobre sus derechos de privacidad y cómo la ley lo protege.
            </p>
            <p className="text-neutral-400 leading-relaxed">
              Esta política de privacidad se aplica a la información que recopilamos a través de nuestro sitio web ubicado en agcollective.co y cualquier servicio relacionado proporcionado por AG Collective.
            </p>
          </section>

          {/* Información que recopilamos */}
          <section className="mb-12">
            <h2 className="text-2xl font-medium mb-4 text-white">2. Información que Recopilamos</h2>
            <p className="text-neutral-400 leading-relaxed mb-4">
              Podemos recopilar, usar, almacenar y transferir diferentes tipos de datos personales sobre usted, que hemos agrupado de la siguiente manera:
            </p>
            <ul className="list-disc pl-6 text-neutral-400 space-y-2">
              <li><strong className="text-white">Datos de identidad:</strong> incluye nombre, apellido, nombre de usuario o identificador similar.</li>
              <li><strong className="text-white">Datos de contacto:</strong> incluye dirección de correo electrónico, números de teléfono y dirección de WhatsApp.</li>
              <li><strong className="text-white">Datos de la empresa:</strong> incluye nombre de la empresa, industria y descripción del negocio.</li>
              <li><strong className="text-white">Datos técnicos:</strong> incluye dirección de protocolo de Internet (IP), datos de inicio de sesión, tipo y versión del navegador, configuración de zona horaria y ubicación.</li>
              <li><strong className="text-white">Datos de uso:</strong> incluye información sobre cómo usa nuestro sitio web y servicios.</li>
              <li><strong className="text-white">Datos de marketing:</strong> incluye sus preferencias para recibir marketing de nuestra parte y sus preferencias de comunicación.</li>
            </ul>
          </section>

          {/* Cómo usamos su información */}
          <section className="mb-12">
            <h2 className="text-2xl font-medium mb-4 text-white">3. Cómo Usamos su Información</h2>
            <p className="text-neutral-400 leading-relaxed mb-4">
              Utilizamos la información que recopilamos para los siguientes propósitos:
            </p>
            <ul className="list-disc pl-6 text-neutral-400 space-y-2">
              <li>Proporcionar, operar y mantener nuestros servicios de marketing digital.</li>
              <li>Procesar sus solicitudes de diagnóstico gratuito y consultas.</li>
              <li>Comunicarnos con usted sobre nuestros servicios, incluyendo actualizaciones y soporte.</li>
              <li>Enviarle información de marketing y promocional (con su consentimiento).</li>
              <li>Mejorar y personalizar su experiencia en nuestro sitio web.</li>
              <li>Analizar el uso del sitio web para mejorar nuestros servicios.</li>
              <li>Procesar pagos y gestionar su suscripción.</li>
              <li>Cumplir con obligaciones legales y resolver disputas.</li>
            </ul>
          </section>

          {/* Compartir información */}
          <section className="mb-12">
            <h2 className="text-2xl font-medium mb-4 text-white">4. Compartir su Información</h2>
            <p className="text-neutral-400 leading-relaxed mb-4">
              Podemos compartir sus datos personales con las siguientes categorías de terceros:
            </p>
            <ul className="list-disc pl-6 text-neutral-400 space-y-2">
              <li><strong className="text-white">Proveedores de servicios:</strong> empresas que proporcionan servicios en nuestro nombre, como procesamiento de pagos (Stripe), análisis web y servicios de correo electrónico.</li>
              <li><strong className="text-white">Plataformas de publicidad:</strong> Meta (Facebook/Instagram) y Google para ejecutar campañas publicitarias en su nombre.</li>
              <li><strong className="text-white">Asesores profesionales:</strong> abogados, banqueros, auditores y aseguradores que brindan servicios de consultoría, banca, legales, de seguros y contabilidad.</li>
              <li><strong className="text-white">Autoridades reguladoras:</strong> cuando sea requerido por ley o para proteger nuestros derechos legales.</li>
            </ul>
            <p className="text-neutral-400 leading-relaxed mt-4">
              No vendemos sus datos personales a terceros.
            </p>
          </section>

          {/* Cookies */}
          <section className="mb-12">
            <h2 className="text-2xl font-medium mb-4 text-white">5. Cookies y Tecnologías Similares</h2>
            <p className="text-neutral-400 leading-relaxed mb-4">
              Utilizamos cookies y tecnologías de seguimiento similares para rastrear la actividad en nuestro sitio web y almacenar cierta información. Las cookies son archivos con una pequeña cantidad de datos que pueden incluir un identificador único anónimo.
            </p>
            <p className="text-neutral-400 leading-relaxed mb-4">
              Tipos de cookies que utilizamos:
            </p>
            <ul className="list-disc pl-6 text-neutral-400 space-y-2">
              <li><strong className="text-white">Cookies esenciales:</strong> necesarias para el funcionamiento del sitio web.</li>
              <li><strong className="text-white">Cookies analíticas:</strong> nos ayudan a entender cómo los visitantes interactúan con nuestro sitio.</li>
              <li><strong className="text-white">Cookies de marketing:</strong> utilizadas para rastrear visitantes a través de sitios web con el propósito de mostrar anuncios relevantes.</li>
            </ul>
            <p className="text-neutral-400 leading-relaxed mt-4">
              Puede configurar su navegador para rechazar todas las cookies o para indicar cuándo se está enviando una cookie.
            </p>
          </section>

          {/* Seguridad */}
          <section className="mb-12">
            <h2 className="text-2xl font-medium mb-4 text-white">6. Seguridad de Datos</h2>
            <p className="text-neutral-400 leading-relaxed">
              Hemos implementado medidas de seguridad técnicas y organizativas apropiadas diseñadas para proteger la seguridad de cualquier información personal que procesamos. Sin embargo, a pesar de nuestras salvaguardas y esfuerzos para asegurar su información, ninguna transmisión electrónica a través de Internet o tecnología de almacenamiento de información puede garantizarse al 100% segura. Utilizamos encriptación SSL, almacenamiento seguro y acceso restringido a datos personales.
            </p>
          </section>

          {/* Retención de datos */}
          <section className="mb-12">
            <h2 className="text-2xl font-medium mb-4 text-white">7. Retención de Datos</h2>
            <p className="text-neutral-400 leading-relaxed">
              Conservaremos sus datos personales solo durante el tiempo que sea necesario para los fines establecidos en esta política de privacidad. Conservaremos y usaremos sus datos personales en la medida necesaria para cumplir con nuestras obligaciones legales, resolver disputas y hacer cumplir nuestras políticas. En general, retenemos los datos de clientes durante 5 años después de la terminación del servicio, a menos que la ley requiera un período más largo.
            </p>
          </section>

          {/* Derechos del usuario */}
          <section className="mb-12">
            <h2 className="text-2xl font-medium mb-4 text-white">8. Sus Derechos de Privacidad</h2>
            <p className="text-neutral-400 leading-relaxed mb-4">
              Bajo ciertas circunstancias, usted tiene derechos bajo las leyes de protección de datos en relación con sus datos personales:
            </p>
            <ul className="list-disc pl-6 text-neutral-400 space-y-2">
              <li><strong className="text-white">Derecho de acceso:</strong> solicitar copias de sus datos personales.</li>
              <li><strong className="text-white">Derecho de rectificación:</strong> solicitar que corrijamos cualquier información que crea que es inexacta.</li>
              <li><strong className="text-white">Derecho de supresión:</strong> solicitar que eliminemos sus datos personales bajo ciertas condiciones.</li>
              <li><strong className="text-white">Derecho a restringir el procesamiento:</strong> solicitar que restrinjamos el procesamiento de sus datos personales.</li>
              <li><strong className="text-white">Derecho a la portabilidad de datos:</strong> solicitar que transfiramos sus datos a otra organización o directamente a usted.</li>
              <li><strong className="text-white">Derecho a retirar el consentimiento:</strong> retirar su consentimiento en cualquier momento cuando dependemos del consentimiento para procesar sus datos personales.</li>
            </ul>
            <p className="text-neutral-400 leading-relaxed mt-4">
              Para ejercer cualquiera de estos derechos, contáctenos en hola@agcollective.co. Responderemos a su solicitud dentro de los 30 días.
            </p>
          </section>

          {/* Menores */}
          <section className="mb-12">
            <h2 className="text-2xl font-medium mb-4 text-white">9. Privacidad de Menores</h2>
            <p className="text-neutral-400 leading-relaxed">
              Nuestros servicios no están dirigidos a personas menores de 18 años. No recopilamos intencionalmente información de identificación personal de menores de 18 años. Si usted es padre o tutor y sabe que su hijo nos ha proporcionado datos personales, contáctenos para que podamos tomar las medidas necesarias.
            </p>
          </section>

          {/* Cambios */}
          <section className="mb-12">
            <h2 className="text-2xl font-medium mb-4 text-white">10. Cambios a esta Política</h2>
            <p className="text-neutral-400 leading-relaxed">
              Podemos actualizar nuestra política de privacidad de vez en cuando. Le notificaremos cualquier cambio publicando la nueva política de privacidad en esta página y actualizando la fecha de "última actualización". Se le aconseja revisar esta política de privacidad periódicamente para cualquier cambio.
            </p>
          </section>

          {/* Contacto */}
          <section className="mb-12">
            <h2 className="text-2xl font-medium mb-4 text-white">11. Contáctenos</h2>
            <p className="text-neutral-400 leading-relaxed mb-4">
              Si tiene alguna pregunta sobre esta política de privacidad o nuestras prácticas de privacidad, contáctenos:
            </p>
            <div className="bg-neutral-900/50 border border-white/5 rounded-xl p-6">
              <p className="text-white font-medium mb-2">AG Collective</p>
              <p className="text-neutral-400">Email: hola@agcollective.co</p>
              <p className="text-neutral-400">WhatsApp: +57 300 123 4567</p>
              <p className="text-neutral-400">Ubicación: Ciudad de México, México</p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8">
        <div className="max-w-4xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-neutral-600">© 2024 AG Collective. Todos los derechos reservados.</p>
          <div className="flex gap-6 text-sm text-neutral-600">
            <Link to="/privacy" className="hover:text-white transition-colors" data-testid="privacy-footer-privacy">Política de privacidad</Link>
            <Link to="/terms" className="hover:text-white transition-colors" data-testid="privacy-footer-terms">Términos de servicio</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
